import { logService, LOG_CATEGORIES } from '@/services/logService'

interface CompressionResult {
  file: File
  compressed: boolean
}

type CompressionStreamLike = TransformStream<Uint8Array, Uint8Array>

type CompressionStreamConstructor = new (format: string) => CompressionStreamLike

/**
 * Compress a file with gzip using the CompressionStream API when available.
 * Falls back to the original file when not supported or when compression fails.
 */
export async function compressFileWithGzip(file: File): Promise<CompressionResult> {
  const compressionCtor = (
    globalThis as unknown as { CompressionStream?: CompressionStreamConstructor }
  ).CompressionStream

  if (typeof compressionCtor !== 'function') {
    return { file, compressed: false }
  }

  try {
    const compressionStream = new compressionCtor('gzip')
    const compressedReadableStream = file.stream().pipeThrough(compressionStream)
    const compressedBlob = await new Response(compressedReadableStream).blob()

    const compressedName = file.name.endsWith('.gz') ? file.name : `${file.name}.gz`
    const compressedFile = new File([compressedBlob], compressedName, {
      type: 'application/gzip',
      lastModified: file.lastModified,
    })

    return { file: compressedFile, compressed: true }
  } catch (error) {
    logService.warn(LOG_CATEGORIES.FILE, 'Falling back to uncompressed file', {
      error: error instanceof Error ? error.message : String(error),
      fileName: file.name,
    })
    return { file, compressed: false }
  }
}
